// Save Workspace Modal Component

import React, { useState, useEffect } from 'react';
import { ModalCard, ConfigCard } from '@/components/ui';
import { ConfigField } from '@/components/ui';
import { useSaveWorkspace, useWorkspaceNameExists } from '@/hooks/useWorkspaces';
import { useUpdateWorkspace } from '@/hooks/useUpdateWorkspace';
import { usePermissions } from '@/hooks/usePermissions';
import { MinimalWorkspace } from '@/utils/workspacePermissions';
import { SaveWorkspaceRequest, UpdateWorkspaceRequest, Workspace } from '@/types/workspace';
import { WorkspaceContext } from '@/types/workspaceContext';
import { FailureFilters } from '@/types/filters';
import { FailureRecord } from '@/types/failure';
import { WeibullAnalysisResult } from '@/utils/weibull';

interface SaveWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: FailureFilters;
  currentAnalysisConfig: {
    showConfidenceIntervals: boolean;
    confidenceLevel: number;
    showCensoredData: boolean;
    method?: string; // For VBL analysis method
  };
  currentFailures?: FailureRecord[] | unknown[]; // Support both FailureRecord and VblRecord
  currentResults?: WeibullAnalysisResult | unknown; // Support both WeibullAnalysisResult and VblAnalysisResult
  onSuccess?: (workspace: Workspace) => void;

  // Workspace type for proper categorization
  workspaceType: 'weibull' | 'vbl';

  // Workspace context for update vs create logic
  workspaceContext?: WorkspaceContext;
}

const SaveWorkspaceModal: React.FC<SaveWorkspaceModalProps> = ({
  isOpen,
  onClose,
  currentFilters,
  currentAnalysisConfig,
  currentFailures,
  currentResults,
  onSuccess,
  workspaceType,
  workspaceContext,
}) => {
  // Determine if we're updating an existing workspace
  const isUpdatingExisting = workspaceContext?.isWorkspaceLoaded || false;
  const currentWorkspaceName = workspaceContext?.currentWorkspace?.name || '';
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [changesSummary, setChangesSummary] = useState('');

  const saveWorkspaceMutation = useSaveWorkspace();
  const updateWorkspaceMutation = useUpdateWorkspace();
  const checkNameExistsHook = useWorkspaceNameExists();
  const permissions = usePermissions();

  // Check if user has permission to edit the current workspace
  const canEdit = !isUpdatingExisting || 
    (workspaceContext?.currentWorkspace && workspaceContext.currentWorkspace.id && 
     permissions.canEditWorkspace({
       id: workspaceContext.currentWorkspace.id,
       name: workspaceContext.currentWorkspace.name || '',
       createdBy: { id: permissions.userId, name: permissions.user?.fullName || 'Unknown' }
     } satisfies MinimalWorkspace));

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: isUpdatingExisting ? currentWorkspaceName : '',
        description: '',
        tags: '',
      });
      setChangesSummary(isUpdatingExisting ? '' : '');
      setErrors({});
    }
  }, [isOpen, isUpdatingExisting, currentWorkspaceName]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Workspace name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Workspace name must be at least 3 characters';
    } else if (
      checkNameExistsHook(
        formData.name.trim(),
        isUpdatingExisting ? workspaceContext?.currentWorkspace?.id : undefined
      )
    ) {
      newErrors.name = 'A workspace with this name already exists';
    }

    // Validate description length
    if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (fieldId: string, value: string | number) => {
    const stringValue = value.toString();

    if (fieldId === 'changesSummary') {
      setChangesSummary(stringValue);
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldId]: stringValue,
      }));
    }

    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors((prev) => ({
        ...prev,
        [fieldId]: '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isUpdatingExisting && workspaceContext?.currentWorkspace?.id) {
        // UPDATE EXISTING WORKSPACE (creates version)
        const updateRequest: UpdateWorkspaceRequest = {
          workspace_id: workspaceContext.currentWorkspace.id,
          workspace_name: formData.name.trim() !== currentWorkspaceName ? formData.name.trim() : undefined,
          description: formData.description.trim() || undefined,
          workspace_type: workspaceType,
          configuration: {
            filters: currentFilters,
            analysisParams: currentAnalysisConfig,
          },
          tags: formData.tags
            ? formData.tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean)
            : undefined,
          current_failures: currentFailures ? { failures: currentFailures as FailureRecord[] } : {},
          current_results: (currentResults as WeibullAnalysisResult) || {},
          updated_by: '', // Will be set in the hook
        };

        const updatedWorkspace = await updateWorkspaceMutation.mutateAsync({
          id: workspaceContext.currentWorkspace.id,
          request: updateRequest,
          currentFailures: currentFailures,
          currentResults: currentResults,
        });

        onSuccess?.(updatedWorkspace);
      } else {
        // CREATE NEW WORKSPACE
        const saveRequest: SaveWorkspaceRequest = {
          workspace_name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          workspace_type: workspaceType,
          configuration: {
            filters: currentFilters,
            analysisParams: currentAnalysisConfig,
          },
          tags: formData.tags
            ? formData.tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean)
            : undefined,
          owner: '', // Will be set in the hook
          current_failures: currentFailures ? { failures: currentFailures as FailureRecord[] } : {},
          current_results: (currentResults as WeibullAnalysisResult) || {},
        };

        const savedWorkspace = await saveWorkspaceMutation.mutateAsync({
          request: saveRequest,
          currentFailures: currentFailures, // Always include failure data
          currentResults: currentResults, // Always include cached results
        });

        onSuccess?.(savedWorkspace);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save workspace:', error);
      setErrors({ submit: 'Failed to save workspace. Please try again.' });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const configFields: ConfigField[] = [
    {
      id: 'name',
      label: 'Workspace Name',
      type: 'text' as const,
      value: formData.name,
      placeholder: isUpdatingExisting
        ? currentWorkspaceName
        : 'e.g., Q4 Gearbox Reliability Analysis',
      required: true,
      validation: {
        min: 3,
        message: isUpdatingExisting
          ? 'Leave blank to keep current name'
          : 'Choose a descriptive name for your analysis workspace',
      },
    },
    {
      id: 'description',
      label: 'Description',
      type: 'textarea' as const,
      value: formData.description,
      placeholder: 'Optional: Describe the purpose and scope of this analysis...',
      validation: {
        max: 500,
        message: 'Brief description of your analysis objectives',
      },
    },
    {
      id: 'tags',
      label: 'Tags',
      type: 'text' as const,
      value: formData.tags,
      placeholder: 'e.g., gearbox, bearing, Q4-2024 (comma-separated)',
      validation: {
        message: 'Add tags to organize and find your workspaces easily',
      },
    },
  ];

  // Add changes summary field for updates
  if (isUpdatingExisting) {
    const changesSummaryField: ConfigField = {
      id: 'changesSummary',
      label: 'Changes Summary',
      type: 'text' as const,
      value: changesSummary,
      placeholder: 'e.g., Updated filters to include more sites, Changed confidence level to 99%',
      validation: {
        message: 'Brief summary of what changed in this version',
      },
    };
    configFields.splice(2, 0, changesSummaryField); // Insert before tags
  }

  // Show permission error if user cannot edit
  if (isUpdatingExisting && !canEdit) {
    return (
      <ModalCard
        isOpen={isOpen}
        onClose={onClose}
        title="Permission Denied"
        subtitle="You do not have permission to edit this workspace"
        size="md"
      >
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-900 mb-2">Limited Access</h4>
            <p className="text-sm text-yellow-800">
              You can view and load this workspace, but you cannot edit it because:
            </p>
            <ul className="text-sm text-yellow-800 mt-2 space-y-1">
              {permissions.isOnyxUser() ? null : (
                <>
                  {permissions.isAdmin() ? null : (
                    <li>• You are not an admin user</li>
                  )}
                  {workspaceContext?.currentWorkspace?.id && !permissions.isWorkspaceOwner({
                    id: workspaceContext.currentWorkspace.id,
                    name: workspaceContext.currentWorkspace.name || '',
                    createdBy: { id: permissions.userId, name: permissions.user?.fullName || 'Unknown' }
                  } satisfies MinimalWorkspace) && (
                    <li>• You are not the owner of this workspace</li>
                  )}
                </>
              )}
            </ul>
            <p className="text-sm text-yellow-800 mt-3">
              You can create a new workspace with your current configuration instead.
            </p>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </ModalCard>
    );
  }

  return (
    <ModalCard
      isOpen={isOpen}
      onClose={onClose}
      title={isUpdatingExisting ? 'Update Workspace' : 'Save Workspace'}
      subtitle={
        isUpdatingExisting
          ? `Update "${currentWorkspaceName}" and create a new version.`
          : `Save your complete analysis with all data and results.`
      }
      size="lg"
      closeOnBackdrop={false}
    >
      <ConfigCard
        fields={configFields}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={isUpdatingExisting ? 'Update Workspace' : 'Save Workspace'}
        cancelLabel="Cancel"
        isSubmitting={saveWorkspaceMutation.isLoading || updateWorkspaceMutation.isLoading}
        errors={errors}
      />

      {/* Additional information panel */}
      <div className="px-6 pb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">What gets saved?</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>
              • <strong>Complete Dataset:</strong> All failure records matching your filters
            </li>
            <li>
              • <strong>Analysis Configuration:</strong> Filters, confidence levels, censoring
              options
            </li>
            <li>
              • <strong>Analysis Results:</strong> Pre-calculated Weibull parameters and
              confidence intervals
            </li>
          </ul>
        </div>
      </div>
    </ModalCard>
  );
};

export default SaveWorkspaceModal;
